import { useMemo } from "react";
import { useFireSafetyData, FireSafetyInfo, ActionableIssuesSummary } from "./useFireSafetyData";

export interface CategoryStatusCount {
  category: string;
  ok: number;
  partial: number;
  notOk: number;
  notRequired: number;
  unknown: number;
  total: number;
}

export interface InspectionStatusCount {
  status: string;
  count: number;
  color: string;
}

export interface TypeDistribution {
  type: string;
  count: number;
  percentage: number;
}

export interface ComplianceScore {
  category: string;
  score: number;
  color: string;
}

export interface MaintenanceStatistics {
  // Fire Safety Statistics
  fireSafety: {
    totalItems: number;
    buildings: number;
    units: number;
    categoryStatusCounts: CategoryStatusCount[];
    inspectionStatus: InspectionStatusCount[];
    typeDistribution: TypeDistribution[];
    complianceScores: ComplianceScore[];
    overallComplianceRate: number;
    actionableIssues: ActionableIssuesSummary;
  };
}

// Helper to determine status type
const getStatusType = (status: string): 'ok' | 'partial' | 'notOk' | 'notRequired' | 'unknown' => {
  if (status === 'Aanwezig') return 'ok';
  if (status === 'Gedeeltelijk aanwezig') return 'partial';
  if (status === 'Niet aanwezig' || status === 'Verplicht, en niet aanwezig') return 'notOk';
  if (status === 'Niet nagekeken') return 'unknown';
  // All "Niet nodig", "Niet verplicht", "Geen X verplicht" etc. are not required
  if (status.includes('Niet') || status.includes('Geen')) return 'notRequired';
  return 'unknown';
};

// Calculate compliance score for a category
const calculateComplianceScore = (ok: number, partial: number, notOk: number, unknown: number): number => {
  const applicable = ok + partial + notOk + unknown;
  if (applicable === 0) return 100; // All not required = 100% compliant

  // Score: OK = 100%, Partial = 50%, NotOK = 0%, Unknown = 0%
  const score = ((ok * 100) + (partial * 50)) / applicable;
  return Math.round(score);
};

export function useMaintenanceStatistics() {
  const {
    fireSafetyInfo,
    isLoading,
    error,
    actionableIssues,
    refresh
  } = useFireSafetyData();

  const statistics = useMemo<MaintenanceStatistics | null>(() => {
    if (fireSafetyInfo.length === 0) return null;

    const buildings = fireSafetyInfo.filter(item => item.type === 'building');
    const units = fireSafetyInfo.filter(item => item.type === 'unit');

    // Calculate category status counts
    const categories = [
      {
        name: 'Rookmelders',
        field: 'smokeDetectorsOnEveryFloorStatus',
        items: fireSafetyInfo
      },
      {
        name: 'Brandblussers',
        field: 'fireExtinguisherOnEveryFloorStatus',
        items: buildings
      },
      {
        name: 'Brandhaspel',
        field: 'fireHoseReelStatus',
        items: buildings
      },
      {
        name: 'Noodverlichting',
        field: 'emergencyLightingOnEveryFloorStatus',
        items: buildings
      },
      {
        name: 'Branddeuren',
        field: 'fireProofDoors',
        items: fireSafetyInfo
      },
      {
        name: 'Evacuatie',
        field: 'fireEvacuationStatus',
        items: buildings
      },
      {
        name: 'Brandtrap',
        field: 'fireStairStatus',
        items: buildings
      },
    ];

    const categoryStatusCounts: CategoryStatusCount[] = categories.map(cat => {
      const counts = { ok: 0, partial: 0, notOk: 0, notRequired: 0, unknown: 0 };

      cat.items.forEach(item => {
        const value = item[cat.field as keyof FireSafetyInfo] as string;
        if (value) {
          const statusType = getStatusType(value);
          counts[statusType]++;
        }
      });

      return {
        category: cat.name,
        ...counts,
        total: cat.items.length
      };
    });

    // Calculate compliance scores
    const complianceScores: ComplianceScore[] = categoryStatusCounts.map(cat => {
      const score = calculateComplianceScore(cat.ok, cat.partial, cat.notOk, cat.unknown);
      let color = '#5B7304'; // moss-primary (good)
      if (score < 90) color = '#F59E0B'; // yellow (warning)
      if (score < 70) color = '#EA8162'; // brick-primary (danger)

      return {
        category: cat.category,
        score,
        color
      };
    });

    // Overall compliance rate
    const totalApplicable = categoryStatusCounts.reduce((sum, cat) =>
      sum + cat.ok + cat.partial + cat.notOk + cat.unknown, 0);
    const totalOk = categoryStatusCounts.reduce((sum, cat) => sum + cat.ok, 0);
    const totalPartial = categoryStatusCounts.reduce((sum, cat) => sum + cat.partial, 0);
    const overallComplianceRate = totalApplicable > 0
      ? Math.round(((totalOk * 100) + (totalPartial * 50)) / totalApplicable)
      : 100;

    // Inspection status distribution
    const today = new Date();
    const thirtyDaysFromNow = new Date();
    thirtyDaysFromNow.setDate(today.getDate() + 30);
    const ninetyDaysFromNow = new Date();
    ninetyDaysFromNow.setDate(today.getDate() + 90);

    let expired = 0;
    let dueSoon = 0;
    let dueIn90Days = 0;
    let valid = 0;
    let notScheduled = 0;

    fireSafetyInfo.forEach(item => {
      if (!item.fireSecurityNextInspectionDate) {
        notScheduled++;
      } else {
        const nextDate = new Date(item.fireSecurityNextInspectionDate);
        if (nextDate < today) {
          expired++;
        } else if (nextDate <= thirtyDaysFromNow) {
          dueSoon++;
        } else if (nextDate <= ninetyDaysFromNow) {
          dueIn90Days++;
        } else {
          valid++;
        }
      }
    });

    const inspectionStatus: InspectionStatusCount[] = [
      { status: 'Vervallen', count: expired, color: '#EA8162' },
      { status: 'Binnen 30 dagen', count: dueSoon, color: '#F59E0B' },
      { status: 'Binnen 90 dagen', count: dueIn90Days, color: '#3B82F6' },
      { status: 'Geldig', count: valid, color: '#5B7304' },
      { status: 'Niet ingepland', count: notScheduled, color: '#838C89' },
    ];

    // Type distribution
    const typeDistribution: TypeDistribution[] = [
      {
        type: 'Gebouwen',
        count: buildings.length,
        percentage: Math.round((buildings.length / fireSafetyInfo.length) * 100)
      },
      {
        type: 'Units',
        count: units.length,
        percentage: Math.round((units.length / fireSafetyInfo.length) * 100)
      },
    ];

    return {
      fireSafety: {
        totalItems: fireSafetyInfo.length,
        buildings: buildings.length,
        units: units.length,
        categoryStatusCounts,
        inspectionStatus,
        typeDistribution,
        complianceScores,
        overallComplianceRate,
        actionableIssues
      }
    };
  }, [fireSafetyInfo, actionableIssues]);

  return {
    statistics,
    isLoading,
    error,
    refresh
  };
}
